/* ============================================================
 * El pase de acceso.
 *
 * Es lo único que el cliente lleva encima al centro, así que tiene que
 * funcionar en el peor caso: sin cobertura, con la pantalla agrietada o
 * con el móvil casi sin batería. De ahí las tres vías redundantes:
 *
 *   1. El QR, que es lo rápido cuando la cámara del mostrador va bien.
 *   2. El código corto, para dictarlo o teclearlo cuando no va.
 *   3. La descarga como imagen, que sobrevive a quedarse sin datos.
 *
 * El enlace permanente cubre el cuarto caso: no haber guardado nada.
 * ============================================================ */

import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { clp, fechaLarga } from '../utiles/fechas.js';

export function Pase({ pase, sede, fecha, hora, equipo, cliente, precioFinal = null }) {
  const lienzoQR = useRef(null);
  const [urlPase, setUrlPase] = useState('');
  const [descargando, setDescargando] = useState(false);

  const enlace = `${window.location.origin}/pase/${pase.token}`;

  useEffect(() => {
    if (!lienzoQR.current) return;
    // Corrección de errores alta: un QR impreso o en una pantalla rayada
    // sigue leyéndose con parte del dibujo dañada.
    QRCode.toCanvas(lienzoQR.current, enlace, {
      width: 220, margin: 1, errorCorrectionLevel: 'H',
      color: { dark: '#1A1A1A', light: '#FFFFFF' },
    }).then(() => setUrlPase(lienzoQR.current.toDataURL('image/png')))
      .catch(() => { /* si falla el QR, quedan el código y el enlace */ });
  }, [enlace]);

  /** Compone el pase entero como imagen, no solo el QR suelto. */
  const descargar = async () => {
    setDescargando(true);
    try {
      const qr = await QRCode.toDataURL(enlace, {
        width: 420, margin: 1, errorCorrectionLevel: 'H',
        color: { dark: '#1A1A1A', light: '#FFFFFF' },
      });
      const lienzo = document.createElement('canvas');
      lienzo.width = 720;
      lienzo.height = 1180;
      const g = lienzo.getContext('2d');

      g.fillStyle = '#FCF7F2';
      g.fillRect(0, 0, 720, 1180);

      g.fillStyle = '#1A1A1A';
      g.textAlign = 'center';
      g.font = '300 46px Georgia, serif';
      g.letterSpacing = '14px';
      g.fillText('TUNE', 360, 110);
      g.letterSpacing = '0px';

      g.fillStyle = '#8A7E7A';
      g.font = '500 17px system-ui, sans-serif';
      g.letterSpacing = '3px';
      g.fillText('PASE DE SESIÓN', 360, 152);
      g.letterSpacing = '0px';

      const imagenQR = new Image();
      imagenQR.src = qr;
      await new Promise((listo) => { imagenQR.onload = listo; });
      g.fillStyle = '#FFFFFF';
      g.fillRect(150, 200, 420, 420);
      g.drawImage(imagenQR, 150, 200, 420, 420);

      g.fillStyle = '#1A1A1A';
      g.font = '600 54px ui-monospace, Menlo, monospace';
      g.fillText(pase.codigo, 360, 706);

      g.fillStyle = '#8A7E7A';
      g.font = '400 16px system-ui, sans-serif';
      g.fillText('Enseña este código si el escáner no funciona', 360, 740);

      const filas = [
        ['Centro', sede.nombre],
        ['Dirección', sede.direccion],
        ['Día', fechaLarga(fecha)],
        ['Hora', hora],
        ['Equipo', equipo],
        ['A nombre de', cliente],
      ];
      let y = 820;
      for (const [clave, valor] of filas) {
        g.textAlign = 'left';
        g.fillStyle = '#8A7E7A';
        g.font = '400 16px system-ui, sans-serif';
        g.fillText(clave, 90, y);
        g.textAlign = 'right';
        g.fillStyle = '#1A1A1A';
        g.font = '500 18px system-ui, sans-serif';
        g.fillText(String(valor), 630, y);
        y += 44;
      }

      g.textAlign = 'center';
      g.fillStyle = '#8A7E7A';
      g.font = '400 15px system-ui, sans-serif';
      g.fillText(`El pago se realiza en el centro · ${clp(precioFinal ?? sede.precio_tune)}`, 360, y + 26);

      const enlaceDescarga = document.createElement('a');
      enlaceDescarga.download = `pase-tune-${pase.codigo}.png`;
      enlaceDescarga.href = lienzo.toDataURL('image/png');
      enlaceDescarga.click();
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="pase">
      <div className="pase-qr">
        <canvas ref={lienzoQR} />
      </div>

      <div className="pase-codigo">
        <div className="pc-label">O enseña este código</div>
        <div className="pc-valor">{pase.codigo}</div>
      </div>

      <div className="pase-acciones">
        <button className="btn block" onClick={descargar} disabled={descargando}>
          {descargando ? 'Preparando…' : 'Descargar mi pase'}
        </button>
        <button className="link-btn" onClick={() => navigator.clipboard?.writeText(enlace)}>
          Copiar el enlace del pase
        </button>
      </div>

      <p className="pase-nota">
        Guarda el pase o el enlace. Si lo pierdes, en el centro pueden encontrar tu
        reserva por tu nombre.
      </p>

      {urlPase ? null : null}
    </div>
  );
}
